import "@/App.css";
import "@/index.css";
import { useState, useEffect, useRef, useCallback } from "react";
import { motion, useInView, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { 
  ArrowRight, MessageCircle, Menu, X, 
  BarChart3, TrendingUp, Globe, 
  Quote, Target, Settings, FileText,
  Phone, Mail, ChevronDown, ExternalLink, CheckCircle2,
  Zap, Users, Building2, Briefcase, LineChart, Search,
  Lightbulb, Layers, ArrowRightCircle, Cog, ShoppingCart,
  UserCheck, ClipboardCheck, Rocket, BookOpen, Award,
  Dumbbell, Utensils, Stethoscope, Wrench, User, Send,
  TrendingDown, AlertCircle, Eye, HelpCircle, GraduationCap,
  Handshake, PenTool, Database, FileSearch, Link2
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const CONTACT_EMAIL = "nitiagrowthsystems@gmail.com";
const EMAIL_SUBJECT = encodeURIComponent("Growth Diagnostic Request");
const EMAIL_BODY = encodeURIComponent(`Hi NITIA Team,

I'd like to request a business growth diagnostic.

Company Name:
Industry:
Current Annual Revenue (approx):
Biggest Growth Challenge:

Looking forward to hearing from you.`);
const EMAIL_LINK = `mailto:${CONTACT_EMAIL}?subject=${EMAIL_SUBJECT}&body=${EMAIL_BODY}`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_nitia-audit-engine/artifacts/5v51flwb_ChatGPT%20Image%20Feb%2021%2C%202026%2C%2003_00_15%20PM.png";

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 }
  }
};

const slideFromLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { 
    opacity: 1, 
    x: 0, 
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } 
  }
};

const slideFromRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { 
    opacity: 1, 
    x: 0, 
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } 
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } 
  }
};

// Shadow Text Animation
const ShadowTextReveal = ({ children, delay = 0, className = "" }) => (
  <motion.span
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
    className={`inline-block ${className}`}
  >
    {children}
  </motion.span>
);

// Planet Cursor Component
const PlanetCursor = () => {
  const cursorRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isClicking, setIsClicking] = useState(false);

  const moveCursor = useCallback((e) => {
    if (cursorRef.current) {
      cursorRef.current.style.left = `${e.clientX}px`;
      cursorRef.current.style.top = `${e.clientY}px`;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', moveCursor);
    
    const handleMouseOver = (e) => {
      const target = e.target;
      if (
        target.tagName === 'A' ||
        target.tagName === 'BUTTON' ||
        target.closest('a') ||
        target.closest('button') ||
        target.classList.contains('card') ||
        target.closest('.card')
      ) {
        setIsHovering(true);
      }
    };

    const handleMouseOut = () => setIsHovering(false);
    const handleMouseDown = () => setIsClicking(true);
    const handleMouseUp = () => setIsClicking(false);

    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', moveCursor);
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [moveCursor]);

  return (
    <div 
      ref={cursorRef}
      className={`planet-cursor ${isHovering ? 'hovering' : ''} ${isClicking ? 'clicking' : ''}`}
    >
      <div className="planet-cursor-ring"><span /></div>
      <div className="planet-cursor-core" />
    </div>
  );
};

// Navigation Data with Dropdowns
const NAV_DATA = {
  insights: {
    label: "Insights",
    type: "mega",
    items: [
      { id: "strategic-breakdowns", title: "Case Breakdowns", description: "Strategic analysis of real companies" },
      { id: "strategic-breakdowns", title: "Revenue Growth Systems", description: "How businesses build scalable growth" },
      { id: "strategic-breakdowns", title: "Customer Acquisition", description: "Frameworks for attracting customers" },
      { id: "strategic-breakdowns", title: "Market Expansion", description: "Strategies for entering new markets" }
    ]
  },
  howWeWork: {
    label: "How We Work",
    type: "light",
    items: [
      { id: "how-we-work", title: "Our Process", description: "From business deep dive to execution" },
      { id: "faq", title: "Common Questions", description: "FAQs about our consulting process" }
    ]
  },
  about: {
    label: "About Us",
    type: "light",
    items: [
      { id: "about", title: "About Us", description: "How we think about business growth" },
      { id: "how-we-think", title: "Our Approach", description: "Our strategic framework" },
      { id: "who-we-help", title: "Who We Work With", description: "Businesses we partner with" },
      { id: "selected-work", title: "Client Work", description: "Selected projects and examples" },
      { id: "client-feedback", title: "Client Feedback", description: "Testimonials and experiences" },
      { id: "contact-email", title: "Contact Us", description: "Get in touch via email", isEmail: true }
    ]
  }
};

// Dropdown Component
const NavDropdown = ({ data, isScrolled, onItemClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 150);
  };

  return (
    <div 
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button 
        className={`nav-link flex items-center gap-1.5 ${isScrolled ? 'text-[#0A1628] hover:text-[#0062D6]' : 'text-white/80 hover:text-white'}`}
        data-testid={`nav-dropdown-${data.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {data.label}
        <ChevronDown size={14} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="nav-dropdown"
            data-testid={`dropdown-${data.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className={`nav-dropdown-content ${data.type === 'mega' ? 'nav-dropdown-mega' : ''}`}>
              {data.items.map((item) => (
                <a
                  key={item.title}
                  href={item.isEmail ? `mailto:${CONTACT_EMAIL}` : `#${item.id}`}
                  onClick={(e) => {
                    if (item.isEmail) {
                      // Allow default mailto behavior
                      setIsOpen(false);
                      return;
                    }
                    e.preventDefault();
                    setIsOpen(false);
                    onItemClick(item.id);
                  }}
                  className="nav-dropdown-item"
                  data-testid={`dropdown-item-${item.id}`}
                >
                  <span className="nav-dropdown-title">{item.title}</span>
                  <span className="nav-dropdown-desc">{item.description}</span>
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Mobile Accordion for Navigation
const MobileNavAccordion = ({ data, onItemClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="mobile-nav-accordion">
      <button 
        className="mobile-nav-accordion-trigger"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {data.label}
        <ChevronDown size={18} className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="mobile-nav-accordion-content"
          >
            {data.items.map((item) => (
              <a
                key={item.title}
                href={item.isEmail ? `mailto:${CONTACT_EMAIL}` : `#${item.id}`}
                onClick={(e) => {
                  if (item.isEmail) {
                    // Allow default mailto behavior
                    return;
                  }
                  e.preventDefault();
                  onItemClick(item.id);
                }}
                className="mobile-nav-accordion-item"
              >
                <span className="mobile-nav-accordion-title">{item.title}</span>
                <span className="mobile-nav-accordion-desc">{item.description}</span>
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Navbar with Premium Dropdowns
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (id) => {
    setMobileMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.nav
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className={`navbar ${isScrolled ? "navbar-solid" : "navbar-transparent"}`}
        data-testid="navbar"
      >
        <div className="container">
          <div className="navbar-inner">
            {/* Logo */}
            <motion.a href="#" className="flex items-center gap-3" whileHover={{ scale: 1.02 }} data-testid="logo">
              <img src={LOGO_URL} alt="NITIA Logo" className="h-12 w-auto" />
              <div className="flex flex-col">
                <span className={`font-heading text-xl font-bold leading-tight ${isScrolled ? 'text-[#0A1628]' : 'text-white'}`}>NITIA</span>
                <span className={`text-[10px] tracking-widest uppercase ${isScrolled ? 'text-[#004BA8]' : 'text-white/60'}`}>Growth Systems</span>
              </div>
            </motion.a>

            {/* Desktop Navigation with Dropdowns */}
            <nav className="hidden lg:flex items-center gap-8">
              <NavDropdown data={NAV_DATA.insights} isScrolled={isScrolled} onItemClick={handleNavClick} />
              <NavDropdown data={NAV_DATA.howWeWork} isScrolled={isScrolled} onItemClick={handleNavClick} />
              <NavDropdown data={NAV_DATA.about} isScrolled={isScrolled} onItemClick={handleNavClick} />
              <motion.a 
                href="#diagnostic" 
                onClick={(e) => { e.preventDefault(); handleNavClick('diagnostic'); }}
                className="nav-cta" 
                whileHover={{ scale: 1.05 }} 
                whileTap={{ scale: 0.98 }} 
                data-testid="nav-cta"
              >
                Get Your Growth Diagnosis<ArrowRight size={16} />
              </motion.a>
            </nav>

            {/* Mobile Menu Button */}
            <motion.button 
              className={`lg:hidden p-2 rounded-full ${isScrolled ? 'text-[#0A1628] bg-[#E0EFFF]' : 'text-white bg-white/10'}`} 
              onClick={() => setMobileMenuOpen(true)} 
              whileHover={{ scale: 1.1 }} 
              data-testid="mobile-menu-btn"
            >
              <Menu size={24} />
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu with Accordions */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="mobile-menu-overlay" 
            data-testid="mobile-menu"
          >
            <div className="mobile-menu-header">
              <div className="flex items-center gap-3">
                <img src={LOGO_URL} alt="NITIA Logo" className="h-8 w-auto" />
                <span className="font-heading text-xl font-bold text-[#0A1628]">NITIA</span>
              </div>
              <motion.button 
                className="mobile-menu-close" 
                onClick={() => setMobileMenuOpen(false)} 
                whileHover={{ scale: 1.1, rotate: 90 }}
              >
                <X size={24} />
              </motion.button>
            </div>

            <div className="mobile-menu-content">
              <MobileNavAccordion data={NAV_DATA.insights} onItemClick={handleNavClick} />
              <MobileNavAccordion data={NAV_DATA.howWeWork} onItemClick={handleNavClick} />
              <MobileNavAccordion data={NAV_DATA.about} onItemClick={handleNavClick} />
            </div>

            <div className="mobile-menu-footer">
              <motion.a 
                href="#diagnostic" 
                onClick={(e) => { e.preventDefault(); handleNavClick('diagnostic'); }}
                className="btn-primary w-full justify-center"
                whileHover={{ scale: 1.02 }}
              >
                Get Your Growth Diagnosis<ArrowRight size={18} />
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Galaxy Orbit Animation
const GalaxyOrbit = () => (
  <div className="orbit-container">
    <motion.div className="orbit-ring orbit-ring-1" animate={{ rotate: 360 }} transition={{ duration: 30, repeat: Infinity, ease: "linear" }} />
    <motion.div className="orbit-ring orbit-ring-2" animate={{ rotate: -360 }} transition={{ duration: 25, repeat: Infinity, ease: "linear" }} />
    <motion.div className="orbit-ring orbit-ring-3" animate={{ rotate: 360 }} transition={{ duration: 20, repeat: Infinity, ease: "linear" }} />
    <motion.div className="galaxy-core" animate={{ scale: [1, 1.2, 1], opacity: [0.8, 1, 0.8] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} />
  </div>
);

// Hero Section - Growth Systems Focus with Golden Ratio Layout
const HeroSection = () => (
  <section className="hero" data-testid="hero-section">
    <div className="galaxy-bg" />
    <div className="stars" />
    
    <div className="container hero-content">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left Content */}
        <motion.div className="py-12 md:py-16">
          {/* Brand Name */}
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.2 }} className="text-[#00D4FF] font-medium text-lg mb-4 tracking-wide">
            NITIA Growth Systems
          </motion.p>

          {/* Headline */}
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }} className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6" data-testid="hero-headline">
            We help businesses<br />
            scale revenue <span className="text-[#00D4FF]">with<br />
            structured growth<br />
            systems</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }} className="text-white/70 text-lg max-w-lg mb-8 leading-relaxed" data-testid="hero-subheadline">
            Not random marketing. Not scattered efforts. We diagnose growth bottlenecks, build the right systems, and help businesses grow with clarity.
          </motion.p>

          {/* CTA */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.7 }} className="mb-8">
            <motion.a 
              href="#" 
              onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="floating-audit-trigger"]')?.click(); }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#00D4FF] text-[#0A1628] font-semibold rounded-full hover:bg-[#00D4FF]/90 transition-colors" 
              whileHover={{ scale: 1.03, y: -2 }} 
              whileTap={{ scale: 0.98 }} 
              data-testid="hero-cta"
            >
              Get Your Growth Diagnosis<ArrowRight size={18} />
            </motion.a>
          </motion.div>

          {/* Bottom Text */}
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.9 }} className="text-white/60 text-sm mb-6">
            We don't add more activity to your business. We fix what is already broken.
          </motion.p>

          {/* Trust Line */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 1 }} className="flex flex-wrap items-center gap-3 text-white/50 text-sm">
            <span>Growth bottlenecks</span>
            <span className="w-1 h-1 rounded-full bg-white/50" />
            <span>Revenue systems</span>
            <span className="w-1 h-1 rounded-full bg-white/50" />
            <span>Execution clarity</span>
          </motion.div>
        </motion.div>

        {/* Right Side - Growth Graph */}
        <motion.div 
          initial={{ opacity: 0, x: 50 }} 
          animate={{ opacity: 1, x: 0 }} 
          transition={{ duration: 0.8, delay: 0.5 }}
          className="hidden lg:flex items-center justify-center"
        >
          <div className="relative w-full max-w-md">
            {/* Graph Container */}
            <div className="relative bg-[#0A1628]/50 backdrop-blur-sm border border-white/10 rounded-2xl p-8 aspect-square">
              {/* Grid Lines */}
              <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
                <defs>
                  <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
              </svg>

              {/* Growth Curve */}
              <svg className="absolute inset-8 w-[calc(100%-4rem)] h-[calc(100%-4rem)]" viewBox="0 0 300 300" preserveAspectRatio="none">
                {/* Glow effect */}
                <defs>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                
                {/* Main curve */}
                <path 
                  d="M 20 280 Q 80 260 120 200 Q 160 140 200 100 Q 240 60 280 20" 
                  fill="none" 
                  stroke="#00D4FF" 
                  strokeWidth="3" 
                  strokeLinecap="round"
                  filter="url(#glow)"
                />
                
                {/* Data points */}
                <circle cx="120" cy="200" r="6" fill="#00D4FF" filter="url(#glow)" />
                <circle cx="200" cy="100" r="6" fill="#00D4FF" filter="url(#glow)" />
                <circle cx="280" cy="20" r="6" fill="#00D4FF" filter="url(#glow)" />
              </svg>

              {/* Labels */}
              {/* Bottom Label - Scalable */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.5 }}
                className="absolute bottom-16 left-8 bg-[#0A1628] border border-white/20 rounded-lg px-4 py-3 shadow-xl"
              >
                <p className="text-[10px] text-white/50 uppercase tracking-wider mb-1">Growth Direction</p>
                <p className="text-white font-semibold">Scalable</p>
              </motion.div>

              {/* Middle Label - Predictable */}
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.2, duration: 0.5 }}
                className="absolute top-1/2 left-4 -translate-y-1/2 bg-[#0A1628] border border-white/20 rounded-lg px-4 py-3 shadow-xl"
              >
                <p className="text-[10px] text-white/50 uppercase tracking-wider mb-1">Lead Flow</p>
                <p className="text-white font-semibold">Predictable</p>
              </motion.div>

              {/* Top Label - Structured */}
              <motion.div 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4, duration: 0.5 }}
                className="absolute top-8 right-8 bg-[#0A1628] border border-white/20 rounded-lg px-4 py-3 shadow-xl"
              >
                <p className="text-[10px] text-white/50 uppercase tracking-wider mb-1">Revenue Clarity</p>
                <p className="text-white font-semibold">Structured</p>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>

    {/* Scroll Indicator */}
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }} className="scroll-indicator" onClick={() => document.getElementById('how-we-think')?.scrollIntoView({ behavior: 'smooth' })}>
      <ChevronDown size={24} className="text-white/50 animate-bounce" />
    </motion.div>
  </section>
);

// How We Think Section - Strategic Consulting Focus
const HowWeThinkSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const pillars = [
    {
      icon: Target,
      title: "Revenue-First Strategy",
      description: "Every recommendation ties directly to revenue growth. We don't chase vanity metrics—we focus on what drives business results."
    },
    {
      icon: Layers,
      title: "Systems Over Tactics",
      description: "We design scalable systems that compound over time, not one-off campaigns that fade. Structure creates sustainability."
    },
    {
      icon: LineChart,
      title: "Data-Driven Decisions",
      description: "Our strategies are grounded in analysis. We identify bottlenecks, measure what matters, and optimize continuously."
    },
    {
      icon: Cog,
      title: "End-to-End Execution",
      description: "From strategy to implementation, we work alongside your team to ensure ideas translate into tangible outcomes."
    }
  ];

  return (
    <section id="how-we-think" ref={ref} className="section bg-white" data-testid="how-we-think-section">
      <div className="container">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mb-16">
            <span className="section-label">Our Approach</span>
            <h2 className="section-title">How We Think</h2>
            <p className="section-description mx-auto">
              We're not a marketing agency. We're a strategic partner focused on building the systems that drive predictable revenue growth.
            </p>
          </motion.div>

          {/* Core Pillars Grid */}
          <motion.div variants={staggerContainer} initial="hidden" animate={isInView ? "visible" : "hidden"} className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            {pillars.map((pillar, i) => (
              <motion.div 
                key={i} 
                variants={fadeInUp} 
                className="p-6 rounded-2xl bg-[#F8FAFC] border border-[#E0EFFF] hover:border-[#00D4FF]/50 transition-colors"
                whileHover={{ y: -4 }}
                data-testid={`how-we-think-pillar-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0A1628] to-[#003573] flex items-center justify-center mb-4">
                  <pillar.icon size={22} className="text-[#00D4FF]" />
                </div>
                <h3 className="font-heading text-lg font-bold text-[#0A1628] mb-2">{pillar.title}</h3>
                <p className="text-[#004BA8] leading-relaxed">{pillar.description}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Differentiator Statement */}
          <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center p-8 rounded-2xl bg-gradient-to-r from-[#0A1628] to-[#003573]">
            <p className="text-white/90 text-lg md:text-xl leading-relaxed mb-6">
              Most businesses don't struggle because of effort—<br className="hidden md:block" />
              <span className="text-[#00D4FF] font-semibold">they struggle because of lack of structure.</span>
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <span className="flex items-center gap-2 text-white/70">
                <CheckCircle2 size={16} className="text-[#00D4FF]" />
                Strategy-led
              </span>
              <span className="flex items-center gap-2 text-white/70">
                <CheckCircle2 size={16} className="text-[#00D4FF]" />
                System-driven
              </span>
              <span className="flex items-center gap-2 text-white/70">
                <CheckCircle2 size={16} className="text-[#00D4FF]" />
                Revenue-focused
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Who We Work With Section (Soft Filter - Professional)
const WhoWeHelpSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const idealClientTraits = [
    "Have a proven product or service",
    "Are looking to grow in a more predictable and structured way",
    "Want to build systems, not rely on ad-hoc efforts",
    "Value strategy as much as execution"
  ];

  return (
    <section id="who-we-help" ref={ref} className="section bg-white" data-testid="who-we-help-section">
      <div className="container">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mb-12">
            <span className="section-label">Our Clients</span>
            <h2 className="section-title">Who We Work With</h2>
            <p className="text-[#004BA8] text-lg mt-4">We partner with businesses that are ready to scale with clarity, structure, and long-term thinking.</p>
          </motion.div>

          {/* Ideal Client Profile */}
          <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="mb-12">
            <p className="text-[#0A1628] text-lg mb-6 text-center">We work best with companies that:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {idealClientTraits.map((trait, i) => (
                <motion.div 
                  key={i} 
                  className="flex items-center gap-4 p-5 rounded-xl bg-[#F8FAFC] border border-[#E0EFFF]"
                  whileHover={{ y: -2, borderColor: '#00D4FF' }}
                >
                  <CheckCircle2 size={22} className="text-[#0062D6] flex-shrink-0" />
                  <span className="text-[#0A1628] font-medium">{trait}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* A Note on Fit */}
          <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="p-6 rounded-xl bg-[#F8FAFC] border border-[#E0EFFF]">
            <h4 className="font-heading font-semibold text-[#0A1628] mb-2">A Note on Fit</h4>
            <p className="text-[#004BA8]">Our approach is designed for businesses focused on building long-term, scalable growth systems rather than quick fixes.</p>
          </motion.div>

          {/* CTA */}
          <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mt-10">
            <motion.a href="#how-we-work" className="inline-flex items-center gap-2 text-[#0062D6] font-medium hover:gap-3 transition-all">
              See How We Work<ArrowRight size={18} />
            </motion.a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// How We Work Section (Process)
const HowWeWorkSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const steps = [
    {
      number: "01",
      title: "Business Deep Dive",
      description: "We start by understanding your business model, market position, current growth challenges, and revenue structure."
    },
    {
      number: "02",
      title: "Strategic Blueprint",
      description: "Based on our analysis, we create a clear strategic roadmap identifying the highest-impact growth opportunities."
    },
    {
      number: "03",
      title: "System Design & Execution",
      description: "We design and implement the revenue, acquisition, and operational systems needed to execute the strategy."
    },
    {
      number: "04",
      title: "Optimization & Scale",
      description: "We continuously refine systems based on data, optimizing for efficiency and preparing for sustainable scale."
    }
  ];

  return (
    <section id="how-we-work" ref={ref} className="section bg-light" data-testid="how-we-work-section">
      <div className="container">
        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mb-16">
          <span className="section-label">Our Process</span>
          <h2 className="section-title">How We Work</h2>
          <p className="section-description mx-auto">A structured approach to building growth systems that scale.</p>
        </motion.div>

        <motion.div variants={staggerContainer} initial="hidden" animate={isInView ? "visible" : "hidden"} className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 top-0 bottom-0 w-px bg-gradient-to-b from-[#00D4FF] via-[#0062D6] to-[#A855F7] hidden md:block" />

            {/* Steps */}
            <div className="space-y-8">
              {steps.map((step, i) => (
                <motion.div 
                  key={i} 
                  variants={fadeInUp}
                  className="relative flex gap-6 md:gap-10"
                >
                  {/* Number */}
                  <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-white border-2 border-[#E0EFFF] flex items-center justify-center shadow-sm z-10">
                    <span className="font-heading text-xl font-bold text-[#0062D6]">{step.number}</span>
                  </div>

                  {/* Content */}
                  <div className="flex-1 pb-8">
                    <h3 className="font-heading text-xl font-bold text-[#0A1628] mb-2">{step.title}</h3>
                    <p className="text-[#004BA8] leading-relaxed">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mt-12">
          <motion.a href="#diagnostic" className="btn-primary" whileHover={{ scale: 1.03 }}>
            Book a Strategy Call<ArrowRight size={18} />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

// Selected Work Section (Client Proof)
const SelectedWorkSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="selected-work" ref={ref} className="section bg-white" data-testid="selected-work-section">
      <div className="container">
        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mb-16">
          <span className="section-label">Our Work</span>
          <h2 className="section-title">Selected Work</h2>
          <p className="section-description mx-auto">A glimpse into the businesses we've worked with and the kind of systems we help build.</p>
        </motion.div>

        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="max-w-6xl mx-auto">
          <div className="bg-[#F8FAFC] rounded-2xl border border-[#E0EFFF] overflow-hidden" data-testid="client-card-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Content - Left Side */}
              <div className="p-8 lg:p-10">
                {/* Client Info */}
                <div className="mb-8">
                  <h3 className="font-heading text-2xl md:text-3xl font-bold text-[#0A1628] mb-2">Perfect Electrical Solutions</h3>
                  <p className="text-[#666666]">Electrical Services / Industrial Solutions</p>
                </div>

                {/* The Situation */}
                <div className="mb-8">
                  <h4 className="font-heading font-semibold text-[#0A1628] mb-3 text-lg">The Situation</h4>
                  <p className="text-[#004BA8] leading-relaxed">
                    A well-established electrical services company with strong on-ground work but unclear digital presence. Their services were difficult to understand online, and potential clients couldn't easily see the value they offered.
                  </p>
                </div>

                {/* What We Identified */}
                <div className="mb-8">
                  <h4 className="font-heading font-semibold text-[#0A1628] mb-3 text-lg">What We Identified</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-3 text-[#004BA8]">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#00D4FF] mt-2 flex-shrink-0" />
                      <span>Unclear service positioning — visitors didn't understand what they did</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#004BA8]">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#00D4FF] mt-2 flex-shrink-0" />
                      <span>No structured way to communicate credibility and experience</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#004BA8]">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#00D4FF] mt-2 flex-shrink-0" />
                      <span>Website lacked clarity and professionalism</span>
                    </li>
                  </ul>
                </div>

                {/* What We Did */}
                <div className="mb-8">
                  <h4 className="font-heading font-semibold text-[#0A1628] mb-3 text-lg">What We Did</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-3 text-[#004BA8]">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#00D4FF] mt-2 flex-shrink-0" />
                      <span>Redesigned and restructured the website for clarity</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#004BA8]">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#00D4FF] mt-2 flex-shrink-0" />
                      <span>Defined clear service categories and value propositions</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#004BA8]">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#00D4FF] mt-2 flex-shrink-0" />
                      <span>Built a digital presence that reflects their expertise</span>
                    </li>
                  </ul>
                </div>

                {/* Outcome */}
                <div className="mb-8">
                  <h4 className="font-heading font-semibold text-[#0A1628] mb-3 text-lg">Outcome</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-3 text-[#004BA8]">
                      <CheckCircle2 size={16} className="text-green-500 mt-1 flex-shrink-0" />
                      <span>Professional, structured website that builds trust</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#004BA8]">
                      <CheckCircle2 size={16} className="text-green-500 mt-1 flex-shrink-0" />
                      <span>Clear communication of services and experience</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#004BA8]">
                      <CheckCircle2 size={16} className="text-green-500 mt-1 flex-shrink-0" />
                      <span>Stronger foundation for online customer acquisition</span>
                    </li>
                  </ul>
                </div>

                {/* CTA */}
                <motion.a 
                  href="https://perfectelectricalsolutions.co.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#0062D6] font-semibold hover:gap-3 transition-all"
                  whileHover={{ x: 3 }}
                >
                  View Project<ExternalLink size={16} />
                </motion.a>
              </div>

              {/* Image - Right Side - Website Screenshot Mockup */}
              <div className="relative h-80 lg:h-auto min-h-[400px] bg-gradient-to-br from-slate-800 to-slate-900 p-6 flex items-center justify-center">
                {/* Browser Mockup */}
                <div className="w-full max-w-md bg-white rounded-lg shadow-2xl overflow-hidden">
                  {/* Browser Header */}
                  <div className="bg-slate-200 px-4 py-2 flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-400" />
                      <div className="w-3 h-3 rounded-full bg-yellow-400" />
                      <div className="w-3 h-3 rounded-full bg-green-400" />
                    </div>
                    <div className="flex-1 bg-white rounded px-3 py-1 text-xs text-slate-500 truncate ml-2">
                      perfectelectricalsolutions.co.in
                    </div>
                  </div>
                  {/* Website Preview */}
                  <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-4">
                    <div className="space-y-3">
                      <span className="inline-block bg-blue-500/30 text-blue-300 px-3 py-1 rounded-full text-[10px] border border-blue-400/30">
                        Trusted Industrial Automation Partner
                      </span>
                      <h4 className="text-white text-lg font-bold leading-tight">
                        Perfect Electrical<br />
                        <span className="text-blue-400">Solutions</span>
                      </h4>
                      <p className="text-slate-400 text-[10px] leading-relaxed">
                        Professional Control Panel Manufacturing, PLC Programming, VFD Solutions...
                      </p>
                      <div className="flex gap-2 pt-2">
                        <span className="bg-blue-600 text-white text-[8px] px-2 py-1 rounded">Call Now</span>
                        <span className="bg-green-600 text-white text-[8px] px-2 py-1 rounded">WhatsApp</span>
                      </div>
                    </div>
                  </div>
                  {/* Preview of content below */}
                  <div className="bg-slate-50 p-3">
                    <div className="flex gap-2">
                      <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                        <Zap size={14} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="text-[9px] font-semibold text-slate-800">Expert Technical Team</p>
                        <p className="text-[8px] text-slate-500">Experienced engineers...</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// About Us Section (Consulting Style - Descriptive)
const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="about" ref={ref} className="section bg-light" data-testid="about-section">
      <div className="container">
        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="section-label">About Us</span>
            <h2 className="section-title">About Us</h2>
          </div>
          
          <div className="space-y-6">
            <p className="text-lg text-[#0A1628] leading-relaxed">
              We focus on understanding how businesses grow — and where that growth breaks.
            </p>
            
            <p className="text-lg text-[#004BA8] leading-relaxed">
              In many cases, growth does not fail because of lack of effort. It breaks because there is no clear structure behind it. Marketing, operations, and decision-making often work in isolation, leading to inconsistent and unpredictable outcomes.
            </p>
            
            <p className="text-lg text-[#004BA8] leading-relaxed">
              Our work is built around a simple idea:
            </p>
            
            <p className="text-xl font-semibold text-[#0A1628] pl-6 border-l-4 border-[#00D4FF]">
              Growth without structure is unpredictable.
            </p>
            
            <p className="text-lg text-[#004BA8] leading-relaxed">
              We work with businesses to design systems that bring clarity, consistency, and direction to their growth efforts. Instead of isolated activities, we focus on building connected systems that support how a business attracts customers, operates, and scales.
            </p>
            
            <p className="text-lg text-[#004BA8] leading-relaxed">
              Our approach combines strategy, execution, and continuous improvement — not as separate functions, but as one integrated system.
            </p>
            
            <p className="text-lg text-[#0A1628] leading-relaxed font-medium">
              The goal is not just growth, but growth that is sustainable, measurable, and scalable over time.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Client Feedback / Testimonial Section
const ClientFeedbackSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const testimonials = [
    {
      quote: "We always had strong on-ground work, but our online presence did not reflect it. They helped us structure our website in a way that clearly communicates what we do. The difference is visible — it now feels more professional and easier for clients to understand. The process was simple, focused, and practical.",
      name: "Mr. Nilesh Kumawat",
      role: "CEO",
      company: "Perfect Electrical Solutions"
    }
  ];

  return (
    <section id="client-feedback" ref={ref} className="section bg-white" data-testid="client-feedback-section">
      <div className="container">
        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mb-12">
          <span className="section-label">Feedback</span>
          <h2 className="section-title">Client Feedback</h2>
        </motion.div>

        <motion.div variants={staggerContainer} initial="hidden" animate={isInView ? "visible" : "hidden"} className="max-w-3xl mx-auto">
          {testimonials.map((testimonial, i) => (
            <motion.div 
              key={i} 
              variants={fadeInUp}
              className="relative p-8 md:p-10 rounded-2xl bg-gradient-to-br from-[#F8FAFC] to-white border border-[#E0EFFF] shadow-lg"
              data-testid={`testimonial-${i}`}
            >
              {/* Quote Icon */}
              <Quote className="absolute top-6 left-6 text-[#00D4FF]/20" size={48} />
              
              {/* Quote Text */}
              <p className="relative text-lg md:text-xl text-[#0A1628] leading-relaxed mb-8 pl-8 italic">
                "{testimonial.quote}"
              </p>
              
              {/* Attribution */}
              <div className="flex items-center gap-4 pl-8">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#0A1628] to-[#003573] flex items-center justify-center">
                  <span className="text-white font-bold text-lg">{testimonial.name.charAt(0)}</span>
                </div>
                <div>
                  <p className="font-heading font-bold text-[#0A1628]">{testimonial.name}</p>
                  <p className="text-sm text-[#004BA8]">{testimonial.role}, {testimonial.company}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// NITIA Growth Diagnosis Process Section
const DiagnosisSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const steps = [
    { number: "01", icon: Search, title: "Business Analysis", description: "Deep analysis of market position, customer acquisition channels, and internal systems." },
    { number: "02", icon: Target, title: "Bottleneck Identification", description: "Identify the exact constraint limiting your business growth." },
    { number: "03", icon: Layers, title: "System Design", description: "Design marketing, sales, and operational systems to remove bottlenecks." },
    { number: "04", icon: Rocket, title: "Implementation Roadmap", description: "Provide clear strategies or build the systems needed to scale." }
  ];

  return (
    <section id="diagnosis" ref={ref} className="section bg-alt" data-testid="diagnosis-section">
      <div className="container">
        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mb-16">
          <span className="section-label">Our Process</span>
          <h2 className="section-title">The NITIA Growth Diagnosis</h2>
          <p className="section-description mx-auto">A structured approach to identifying and solving your business growth challenges.</p>
        </motion.div>

        {/* Horizontal Process Flow */}
        <div className="relative">
          {/* Connection Line - Desktop */}
          <div className="hidden lg:block absolute top-[60px] left-[12%] right-[12%] h-[2px] bg-gradient-to-r from-[#00D4FF] via-[#3B9EFF] to-[#A855F7]" />
          
          <motion.div variants={staggerContainer} initial="hidden" animate={isInView ? "visible" : "hidden"} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <motion.div key={i} variants={fadeInUp} className="relative text-center" data-testid={`diagnosis-step-${i}`}>
                {/* Step Number Circle */}
                <motion.div 
                  className="w-[80px] h-[80px] mx-auto mb-6 rounded-full bg-gradient-to-br from-[#0A1628] to-[#003573] flex items-center justify-center relative z-10 border-2 border-[#00D4FF]/30"
                  whileHover={{ scale: 1.1, borderColor: "rgba(0, 212, 255, 0.8)" }}
                >
                  <step.icon size={28} className="text-[#00D4FF]" />
                </motion.div>
                
                {/* Arrow between steps - Desktop */}
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-[40px] right-[-20px] z-20">
                    <ArrowRightCircle size={20} className="text-[#00D4FF]" />
                  </div>
                )}

                <span className="text-[#00D4FF] font-bold text-sm tracking-wider mb-2 block">STEP {step.number}</span>
                <h3 className="font-heading text-xl font-bold text-[#0A1628] mb-3">{step.title}</h3>
                <p className="text-[#004BA8] text-sm leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mt-12">
          <motion.a href="#diagnostic" className="btn-primary" whileHover={{ scale: 1.03 }}>
            Start Your Diagnosis<ArrowRight size={18} />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

// NITIA Growth Engine Framework Section
const FrameworkSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const systems = [
    { 
      icon: Users, 
      title: "Customer Acquisition", 
      description: "How new customers find your business. We analyze and optimize your marketing channels, lead generation, and brand visibility.",
      color: "#00D4FF"
    },
    { 
      icon: UserCheck, 
      title: "Sales Conversion", 
      description: "How prospects become paying customers. We design sales processes, follow-up systems, and conversion optimization strategies.",
      color: "#A855F7"
    },
    { 
      icon: Cog, 
      title: "Operational Efficiency", 
      description: "How your business delivers and scales. We streamline workflows, automate processes, and build systems for sustainable growth.",
      color: "#EC4899"
    }
  ];

  return (
    <section id="framework" ref={ref} className="section bg-dark text-white" data-testid="framework-section">
      <div className="container">
        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mb-16">
          <span className="text-[#00D4FF] text-sm font-bold tracking-wider uppercase mb-4 block">Our Framework</span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">The NITIA Growth Engine</h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">Every business grows through three interconnected systems. We analyze and redesign these systems to unlock your growth potential.</p>
        </motion.div>

        <motion.div variants={staggerContainer} initial="hidden" animate={isInView ? "visible" : "hidden"} className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {systems.map((system, i) => (
            <motion.div 
              key={i} 
              variants={fadeInUp} 
              className="relative p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm"
              whileHover={{ y: -8, borderColor: system.color }}
              data-testid={`framework-card-${i}`}
            >
              <div className="w-16 h-16 rounded-xl mb-6 flex items-center justify-center" style={{ backgroundColor: `${system.color}20` }}>
                <system.icon size={28} style={{ color: system.color }} />
              </div>
              <h3 className="font-heading text-xl font-bold mb-4">{system.title}</h3>
              <p className="text-white/60 leading-relaxed">{system.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Framework Summary */}
        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="mt-16 p-8 rounded-2xl bg-gradient-to-r from-[#00D4FF]/10 to-[#A855F7]/10 border border-white/10 text-center">
          <p className="text-lg text-white/80 mb-6">When these three systems work together efficiently, your business achieves predictable, scalable growth.</p>
          <motion.a href="#diagnostic" className="btn-primary" whileHover={{ scale: 1.03 }}>
            Analyze Your Growth Systems<ArrowRight size={18} />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

// Case Study Section
const CaseStudySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="case-study" ref={ref} className="section bg-light" data-testid="case-study-section">
      <div className="container">
        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mb-16">
          <span className="section-label">Results</span>
          <h2 className="section-title">Case Study</h2>
          <p className="section-description mx-auto">Real business transformation through strategic systems.</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Case Study Content */}
          <motion.div variants={slideFromLeft} initial="hidden" animate={isInView ? "visible" : "hidden"}>
            <div className="mb-6">
              <span className="inline-block px-4 py-1 bg-[#00D4FF]/10 text-[#0062D6] text-sm font-medium rounded-full">Industrial Automation</span>
            </div>
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-[#0A1628] mb-6">Perfect Electrical Solutions</h3>
            
            {/* Problem */}
            <div className="mb-6 p-5 rounded-xl bg-red-50 border-l-4 border-red-400">
              <h4 className="font-bold text-red-800 mb-2 flex items-center gap-2"><Target size={18} />The Problem</h4>
              <p className="text-red-700">Low inbound customer inquiries. No structured digital presence. Difficulty communicating technical expertise to potential clients.</p>
            </div>

            {/* Solution */}
            <div className="mb-6 p-5 rounded-xl bg-blue-50 border-l-4 border-blue-400">
              <h4 className="font-bold text-blue-800 mb-2 flex items-center gap-2"><Lightbulb size={18} />Our Solution</h4>
              <p className="text-blue-700">Comprehensive website design showcasing services and expertise. Digital growth system implementation. Clear customer acquisition funnel.</p>
            </div>

            {/* Result */}
            <div className="mb-8 p-5 rounded-xl bg-green-50 border-l-4 border-green-400">
              <h4 className="font-bold text-green-800 mb-2 flex items-center gap-2"><TrendingUp size={18} />The Result</h4>
              <p className="text-green-700">Significantly improved online presence. Increased customer inquiries. Professional brand positioning in the market.</p>
            </div>

            {/* Client Quote */}
            <div className="p-6 rounded-xl bg-white border border-[#E0EFFF] shadow-lg">
              <Quote className="text-[#00D4FF]/30 mb-4" size={32} />
              <p className="text-[#0A1628] italic mb-4 text-justify">"Before working with NITIA, we struggled to explain what we do to potential clients online. They didn't just build us a website—they helped us think differently about how we present our business. Now when customers find us, they already understand our value. That's made all the difference."</p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#0062D6] to-[#00D4FF] flex items-center justify-center text-white font-bold">NK</div>
                <div>
                  <p className="font-bold text-[#0A1628]">Nilesh Kumawat</p>
                  <p className="font-bold text-[#0062D6] text-sm">CEO, Perfect Electrical Solutions</p>
                </div>
              </div>
            </div>

            <motion.a href="https://perfectelectricalsolutions.co.in/" target="_blank" rel="noopener noreferrer" className="btn-outline mt-6 inline-flex" whileHover={{ scale: 1.03 }}>
              View Live Website<ExternalLink size={16} />
            </motion.a>
          </motion.div>

          {/* Mockup */}
          <motion.div variants={slideFromRight} initial="hidden" animate={isInView ? "visible" : "hidden"} className="relative">
            <motion.div className="relative bg-gradient-to-br from-[#0A1628] to-[#003573] rounded-2xl p-4 shadow-2xl" whileHover={{ y: -10 }}>
              <div className="flex gap-2 mb-3">
                <span className="w-3 h-3 rounded-full bg-red-500" />
                <span className="w-3 h-3 rounded-full bg-yellow-500" />
                <span className="w-3 h-3 rounded-full bg-green-500" />
              </div>
              <div className="aspect-[16/10] rounded-lg overflow-hidden bg-white">
                <img src="https://customer-assets.emergentagent.com/job_power-pro-india/artifacts/mznr8hoc_20250506-electris-hero.jpg" alt="Perfect Electrical Solutions Website" className="w-full h-full object-cover object-top" />
              </div>
            </motion.div>
            {/* Mobile Mockup */}
            <motion.div className="absolute -bottom-6 -right-6 w-32 md:w-40 bg-gradient-to-br from-[#003573] to-[#0A1628] rounded-xl p-2 shadow-xl" animate={{ y: [0, -8, 0] }} transition={{ duration: 4, repeat: Infinity }}>
              <div className="w-12 h-1.5 bg-white/30 rounded-full mx-auto mb-2" />
              <div className="aspect-[9/16] rounded-lg overflow-hidden bg-white">
                <img src="https://customer-assets.emergentagent.com/job_power-pro-india/artifacts/qvh0yfrq_image.png" alt="Mobile View" className="w-full h-full object-cover object-top" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Strategic Case Breakdowns Section (Premium Global Case Studies)
const StrategicCaseBreakdowns = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [selectedCase, setSelectedCase] = useState(null);

  const globalCaseStudies = [
    {
      id: "novo-nordisk",
      company: "Novo Nordisk",
      industry: "Pharmaceuticals",
      category: "Market Expansion",
      tags: ["Market Expansion", "Revenue Growth System", "Category Creation"],
      title: "How Novo Nordisk built a global obesity category — and is now defending it beyond patents",
      hook: "Building a multi-billion dollar category around biological constraints, then transitioning from innovation-led growth to system-led defense.",
      color: "#0062D6",
      atAGlance: {
        industry: "Pharmaceuticals",
        focus: "Market Expansion & Category Creation",
        coreMove: "System design around GLP-1 biology limitations",
        outcome: "Multi-billion dollar global obesity category"
      },
      opportunity: `Novo Nordisk identified one of the largest underdeveloped healthcare markets: obesity.

While most companies treated obesity as a lifestyle issue, Novo Nordisk saw it as a long-term pharmaceutical category with massive global demand.

At the core of this opportunity was GLP-1 biology — a pathway that could regulate blood sugar and appetite.

But there was a fundamental limitation:

GLP-1 based molecules (like semaglutide) are large peptide structures and do not easily cross the blood-brain barrier (BBB).

This meant the solution was not straightforward. The drug could not simply act directly on the brain's appetite centers in a traditional way.

This is where the real opportunity existed: Not just solving a biological problem — but building a system around it.`,
      solution: `Novo Nordisk didn't try to "perfect the molecule" alone. It built a complete system around outcomes.

BIOLOGICAL STRATEGY:
Instead of relying on direct BBB penetration, the drug works through peripheral signaling, hormonal pathways, and indirect appetite regulation. The focus shifted from "Where the drug goes" → "What outcome it creates"

PRODUCT STRATEGY:
They engineered semaglutide into weekly injection formats, high-efficacy treatment for both diabetes and obesity, and a solution that fits long-term patient behavior. This turned a treatment into a lifestyle-integrated product.

MARKET STRATEGY:
Novo Nordisk repositioned obesity from lifestyle issue → medical condition, and from niche treatment → mass market opportunity. This created a new global category, not just a product line.

THE SHIFT - PATENT EXPIRY & MARKET DISRUPTION (2026):
In March 2026, semaglutide's patent expired in India — one of the largest and most price-sensitive markets. This triggered entry of 40+ pharmaceutical companies, launch of 50+ generic versions, and price reductions of 50–70% or more. Within days, multiple companies launched lower-cost alternatives, dramatically expanding access and competition.

WHAT CHANGED STRATEGICALLY:
The competitive advantage shifted from patent protection and exclusivity to brand trust, distribution, device quality (pens vs vials), doctor confidence, and patient experience.`,
      impact: `Novo Nordisk didn't just build a successful drug. It:

• Created a multi-billion dollar global category
• Expanded from diabetes → obesity → metabolic health
• Redefined how pharmaceutical growth works

Now, with generics entering, the company is transitioning from Innovation-led growth → System-led defense.

The strongest businesses don't just build products. They build systems that continue to win — even when the advantage disappears.`,
      takeaways: [
        "You don't always need to remove constraints — you can design around them",
        "Category creation is more powerful than competition",
        "The real moat is not always the product — it's the system around it",
        "When exclusivity fades, execution becomes the advantage",
        "Growth shifts from innovation → distribution → experience"
      ]
    },
    {
      id: "amazon",
      company: "Amazon",
      industry: "Technology & Retail",
      category: "Revenue Growth Systems",
      tags: ["Revenue Growth Systems", "Operational Scale"],
      title: "Turning Thin Margins Into Market Dominance",
      hook: "How Amazon built infrastructure that made competing economically impossible.",
      color: "#FF9900",
      opportunity: `In the 1990s, retail was fragmented and e-commerce was uncertain. Most competitors saw online shopping as a channel. Amazon saw it as an infrastructure opportunity.

The insight was not about selling books. It was about building systems that could eventually sell anything, to anyone, faster and cheaper than incumbents.`,
      solution: `Amazon's solution was systematic investment in logistics, technology, and customer experience — often at the expense of short-term profitability.

Fulfillment centers, Prime membership, AWS cloud infrastructure — each piece was designed to create compounding advantages. Prime turned customers into subscribers. AWS turned technology costs into revenue. Fulfillment turned logistics into a competitive moat.`,
      impact: `Amazon transformed from a bookstore into the world's most valuable company by market cap at multiple points in its history.

AWS alone generates more profit than most Fortune 500 companies. Prime has over 200 million members.`,
      takeaways: [
        "Infrastructure investments compound over time",
        "Scale itself becomes a competitive moat",
        "Systems thinking beats product thinking"
      ]
    },
    {
      id: "apple",
      company: "Apple",
      industry: "Consumer Technology",
      category: "Customer Acquisition & Ecosystem",
      tags: ["Customer Acquisition", "Ecosystem Lock-in"],
      title: "Commanding Premium Pricing in Commoditized Markets",
      hook: "How Apple built an ecosystem that makes switching costs higher than price sensitivity.",
      color: "#555555",
      opportunity: `By the mid-2000s, personal computing and mobile phones were becoming commoditized. Most manufacturers competed on specs and price. Apple saw a different opportunity.

The insight was that customers would pay significant premiums for products that were beautifully designed, deeply integrated, and part of a larger ecosystem.`,
      solution: `Apple's solution combined hardware design excellence with ecosystem lock-in.

The iPhone wasn't just a phone — it was the gateway to iCloud, Apple Music, the App Store, Apple Watch, AirPods, and eventually services revenue. Each product made the others more valuable.

Apple also controlled the full stack: chips, software, services. This allowed optimization that competitors using commodity components couldn't match.`,
      impact: `Apple became the world's first company valued at $3 trillion. Services revenue alone exceeds the total revenue of most tech companies.

iPhone maintains over 50% profit share in smartphones despite under 20% market share.`,
      takeaways: [
        "Ecosystem design creates switching costs",
        "Profit share matters more than market share",
        "Brand is an economic moat when backed by product quality"
      ]
    },
    {
      id: "tesla",
      company: "Tesla",
      industry: "Automotive & Energy",
      category: "Market Disruption",
      tags: ["Market Disruption", "Brand Engine"],
      title: "Disrupting a Century-Old Industry From the Outside",
      hook: "How Tesla proved that automotive could be redefined through technology, brand, and vertical integration.",
      color: "#CC0000",
      opportunity: `For a century, the auto industry operated on established rules: dealership networks, incremental innovation, internal combustion dominance. Tesla saw disruption potential.

Electric vehicles weren't new. What was new was the idea that an EV could be desirable, technologically superior, and sold directly to consumers.`,
      solution: `Tesla's solution combined several strategic moves that traditional automakers couldn't easily replicate.

First, start at the top. The Roadster and Model S established Tesla as a premium brand before moving downmarket.

Second, own the stack. Batteries, software, charging infrastructure, insurance — Tesla vertically integrated where others relied on suppliers.

Third, skip the dealers. Direct sales and over-the-air updates created a customer relationship legacy automakers didn't have.`,
      impact: `Tesla became the most valuable car company in the world. It forced every major automaker to accelerate EV plans. It proved that automotive could be disrupted from outside.`,
      takeaways: [
        "Start premium, then move downmarket",
        "Vertical integration enables speed and control",
        "Disrupt the business model, not just the product"
      ]
    },
    {
      id: "zomato",
      company: "Zomato",
      industry: "Food Tech & Logistics",
      category: "Market Expansion",
      tags: ["Market Expansion", "Logistics-led Growth"],
      title: "Scaling Through Logistics Infrastructure",
      hook: "How Zomato expanded across India by building a delivery network that became its competitive moat.",
      color: "#E23744",
      opportunity: `India's food delivery market was largely unorganized. Most restaurants had no delivery capability. The opportunity wasn't just aggregation — it was building the delivery infrastructure itself.

Zomato recognized that whoever controlled the last-mile logistics would control the market.`,
      solution: `Zomato invested heavily in building a fleet of delivery partners across hundreds of cities. This wasn't just an app — it was a logistics network.

The company also expanded into quick commerce (Blinkit acquisition), cloud kitchens, and restaurant management tools. Each move strengthened the core delivery infrastructure.

By owning the logistics layer, Zomato made itself indispensable to both restaurants and customers.`,
      impact: `Zomato became one of India's largest IPOs in tech. It expanded from a restaurant discovery app to a full-stack food and grocery delivery platform.

The delivery network now serves as a foundation for multiple revenue streams.`,
      takeaways: [
        "Infrastructure ownership creates defensibility",
        "Last-mile logistics is often the real product",
        "Platform plays require network effects"
      ]
    },
    {
      id: "reliance-jio",
      company: "Reliance Jio",
      industry: "Telecommunications",
      category: "Pricing & Scale Disruption",
      tags: ["Market Disruption", "Pricing Strategy"],
      title: "Rewriting Industry Economics Through Free",
      hook: "How Jio used aggressive pricing and massive infrastructure investment to capture 400 million subscribers.",
      color: "#0A2885",
      opportunity: `India had expensive mobile data and fragmented telecom players. Most operators competed on incremental improvements. Jio saw an opportunity to reset the market entirely.

The insight was that data usage would explode if prices dropped dramatically. By building a 4G-only network and offering free services initially, Jio could acquire customers faster than anyone thought possible.`,
      solution: `Jio launched with free voice and data for months, then moved to the lowest prices in the market. This forced competitors to consolidate or exit.

The company invested over $30 billion in infrastructure before launch. This wasn't a startup move — it was a war chest strategy that only a conglomerate could execute.

Jio also built an ecosystem: JioTV, JioCinema, JioMart, JioFiber. Each service reinforced customer stickiness.`,
      impact: `Jio acquired 400+ million subscribers in under 5 years. India went from expensive, limited data to one of the cheapest and highest-consumption markets globally.

The telecom industry consolidated from a dozen players to three. Jio fundamentally changed how Indians access the internet.`,
      takeaways: [
        "Aggressive pricing can reset entire industries",
        "Infrastructure investment creates barriers to entry",
        "Ecosystem bundling increases customer lifetime value"
      ]
    },
    {
      id: "nike",
      company: "Nike",
      industry: "Sportswear & Apparel",
      category: "Customer Acquisition",
      tags: ["Customer Acquisition", "Brand-led Growth"],
      title: "Building a Brand That Transcends Products",
      hook: "How Nike made 'Just Do It' a cultural movement that drives customer acquisition without selling.",
      color: "#111111",
      opportunity: `Sportswear was becoming commoditized. Competitors could match technical features. Nike realized that the sustainable advantage wasn't in the shoe — it was in what the shoe represented.

The opportunity was to build a brand so powerful that customers would choose Nike before considering alternatives.`,
      solution: `Nike invested heavily in athlete endorsements, but differently than competitors. It didn't just pay athletes to wear products — it built narratives around them.

Michael Jordan wasn't just wearing Nikes. He was "Air Jordan." The product became the story.

Nike also pioneered emotional advertising. "Just Do It" wasn't about shoes. It was about human potential. This created customer acquisition through aspiration rather than specification.`,
      impact: `Nike became the most valuable sports brand globally, worth over $30 billion in brand value alone.

Customer acquisition costs dropped because the brand did the selling. Athletes and consumers sought out Nike, not the other way around.`,
      takeaways: [
        "Brand equity reduces customer acquisition costs",
        "Emotional positioning beats functional positioning",
        "Stories sell better than specifications"
      ]
    },
    {
      id: "mcdonalds",
      company: "McDonald's",
      industry: "Quick Service Restaurants",
      category: "Operational Systems & Scale",
      tags: ["Operational Systems", "Franchise Scale"],
      title: "Systemizing Success Across 40,000 Locations",
      hook: "How McDonald's built operational systems so precise that quality is identical in Tokyo and Texas.",
      color: "#FFC72C",
      opportunity: `Restaurants traditionally relied on chef skill and local management. Scaling meant quality variance. McDonald's saw that systematic operations could make consistency a competitive advantage.

The insight was that fast food wasn't a restaurant business — it was a systems business.`,
      solution: `McDonald's created the "Speedee Service System" — documenting every process from cooking times to serving procedures.

The company also realized the real business was real estate, not burgers. Franchisees paid for locations McDonald's owned. This created a capital-efficient scaling model.

Training (Hamburger University), supply chain control, and franchise standards meant a Big Mac tastes the same everywhere. Consistency became the brand promise.`,
      impact: `McDonald's operates 40,000+ locations in 100+ countries. It serves 69 million customers daily.

The company owns prime real estate globally worth billions. The franchise model generates high-margin fees with limited operational risk.`,
      takeaways: [
        "Systemization enables quality at scale",
        "Own the platform, license the operations",
        "Consistency itself can be a brand differentiator"
      ]
    }
  ];

  return (
    <section id="strategic-breakdowns" ref={ref} className="section bg-white" data-testid="strategic-breakdowns-section">
      <div className="container">
        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mb-16">
          <span className="section-label">Strategic Thinking</span>
          <h2 className="section-title">Insights & Case Breakdowns</h2>
          <p className="section-description mx-auto">Our perspective on how leading companies build growth systems, expand markets, and create competitive advantage.</p>
        </motion.div>

        {/* Case Study Cards Grid - 4 columns on xl, 3 on lg, 2 on md */}
        <motion.div variants={staggerContainer} initial="hidden" animate={isInView ? "visible" : "hidden"} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
          {globalCaseStudies.map((study, i) => (
            <motion.div 
              key={study.id} 
              variants={fadeInUp} 
              className="strategic-case-card group bg-white rounded-xl border border-[#E8E8E8] p-6 hover:shadow-xl hover:border-[#D0D0D0] transition-all cursor-pointer"
              whileHover={{ y: -6 }}
              onClick={() => setSelectedCase(study)}
              data-testid={`strategic-case-${study.id}`}
            >
              {/* Category Tag */}
              <div className="mb-4">
                <span className="px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full" style={{ backgroundColor: `${study.color}15`, color: study.color }}>
                  {study.category}
                </span>
              </div>

              {/* Company Name */}
              <h3 className="font-heading text-2xl font-bold text-[#0A1628] mb-2">{study.company}</h3>
              
              {/* Title */}
              <h4 className="font-heading text-lg font-semibold text-[#004BA8] mb-4">{study.title}</h4>

              {/* Hook */}
              <p className="text-[#333333] leading-relaxed mb-6">{study.hook}</p>

              {/* CTA */}
              <div className="flex items-center gap-2 text-sm font-semibold group-hover:gap-3 transition-all" style={{ color: study.color }}>
                <span>Read Breakdown</span>
                <ArrowRight size={16} />
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Case Study Detail Modal */}
      <AnimatePresence>
        {selectedCase && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-8 overflow-y-auto bg-black/60 backdrop-blur-sm"
            onClick={() => setSelectedCase(null)}
            data-testid="case-modal-overlay"
          >
            <motion.div 
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.95 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl my-8 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="case-modal-content"
            >
              {/* Modal Header */}
              <div className="sticky top-0 z-10 bg-white border-b border-[#E8E8E8] px-8 py-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="font-heading text-3xl font-bold text-[#0A1628] mb-1">{selectedCase.company}</h2>
                    <p className="text-[#666666]">{selectedCase.industry}</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {selectedCase.tags.map((tag, j) => (
                        <span key={j} className="px-3 py-1 text-xs font-medium rounded-full" style={{ backgroundColor: `${selectedCase.color}10`, color: selectedCase.color }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button 
                    onClick={() => setSelectedCase(null)}
                    className="p-2 rounded-full hover:bg-[#F5F5F5] transition-colors"
                    data-testid="close-case-modal"
                  >
                    <X size={24} className="text-[#666666]" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="px-8 py-8 space-y-10">
                {/* At a Glance (if available) */}
                {selectedCase.atAGlance && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 rounded-xl bg-[#F8FAFC] border border-[#E0EFFF]">
                    <div>
                      <p className="text-xs font-medium text-[#666666] uppercase tracking-wider mb-1">Industry</p>
                      <p className="font-semibold text-[#0A1628]">{selectedCase.atAGlance.industry}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-[#666666] uppercase tracking-wider mb-1">Focus</p>
                      <p className="font-semibold text-[#0A1628]">{selectedCase.atAGlance.focus}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-[#666666] uppercase tracking-wider mb-1">Core Move</p>
                      <p className="font-semibold text-[#0A1628]">{selectedCase.atAGlance.coreMove}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-[#666666] uppercase tracking-wider mb-1">Outcome</p>
                      <p className="font-semibold" style={{ color: selectedCase.color }}>{selectedCase.atAGlance.outcome}</p>
                    </div>
                  </div>
                )}

                {/* The Opportunity */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${selectedCase.color}15` }}>
                      <Eye size={20} style={{ color: selectedCase.color }} />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-[#0A1628]">The Opportunity</h3>
                  </div>
                  <div className="prose prose-slate max-w-none">
                    {selectedCase.opportunity.split('\n\n').map((para, i) => (
                      <p key={i} className="text-[#333333] leading-relaxed mb-4">{para}</p>
                    ))}
                  </div>
                </div>

                {/* The Solution */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${selectedCase.color}15` }}>
                      <Lightbulb size={20} style={{ color: selectedCase.color }} />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-[#0A1628]">The Solution</h3>
                  </div>
                  <div className="prose prose-slate max-w-none">
                    {selectedCase.solution.split('\n\n').map((para, i) => (
                      <p key={i} className="text-[#333333] leading-relaxed mb-4">{para}</p>
                    ))}
                  </div>
                </div>

                {/* The Impact */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${selectedCase.color}15` }}>
                      <TrendingUp size={20} style={{ color: selectedCase.color }} />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-[#0A1628]">The Impact</h3>
                  </div>
                  <div className="prose prose-slate max-w-none">
                    {selectedCase.impact.split('\n\n').map((para, i) => (
                      <p key={i} className="text-[#333333] leading-relaxed mb-4">{para}</p>
                    ))}
                  </div>
                </div>

                {/* Key Takeaways */}
                <div className="bg-gradient-to-br from-[#F8FAFC] to-[#F1F5F9] rounded-xl p-6 border border-[#E0EFFF]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-[#0A1628] flex items-center justify-center">
                      <Lightbulb size={20} className="text-[#00D4FF]" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-[#0A1628]">Key Takeaways for Businesses</h3>
                  </div>
                  <ul className="space-y-3">
                    {selectedCase.takeaways.map((takeaway, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle2 size={18} className="text-[#0062D6] mt-0.5 flex-shrink-0" />
                        <span className="text-[#333333] font-medium">{takeaway}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                <div className="text-center pt-4">
                  <p className="text-[#666666] mb-4">Want strategic analysis for your company?</p>
                  <motion.a 
                    href="#diagnostic" 
                    className="btn-primary inline-flex"
                    whileHover={{ scale: 1.03 }}
                    onClick={() => setSelectedCase(null)}
                  >
                    Request a Growth Diagnostic<ArrowRight size={18} />
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// Case Studies & Business Analyses Section (NEW)
const CaseStudiesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const caseStudies = [
    {
      icon: Dumbbell,
      type: "Fitness Industry",
      title: "Gym Growth Strategy – Indore",
      problem: "Low membership growth and poor retention. Monthly churn rate exceeding 15%.",
      diagnosis: "Weak referral program and inconsistent digital presence. No follow-up system for leads.",
      strategy: "Introduce referral engine with incentives, local business partnerships, and targeted social media funnel with lead nurturing automation.",
      outcome: "Projected 40% increase in membership growth and improved customer retention within 6 months.",
      color: "#00D4FF"
    },
    {
      icon: Utensils,
      type: "F&B Industry",
      title: "Restaurant Revenue Optimization – Bhopal",
      problem: "Declining footfall despite good food quality. Heavy reliance on aggregator platforms eating into margins.",
      diagnosis: "No direct customer relationship. Missing loyalty program. Weak local brand presence.",
      strategy: "Build owned customer database, implement WhatsApp-based ordering, create loyalty rewards program, and local influencer partnerships.",
      outcome: "Expected 25% reduction in aggregator dependency and 30% increase in repeat customers.",
      color: "#A855F7"
    },
    {
      icon: Stethoscope,
      type: "Healthcare",
      title: "Clinic Patient Acquisition – Nagpur",
      problem: "Losing patients to larger hospital chains. Low visibility in local search results.",
      diagnosis: "No online presence strategy. Patient reviews not being collected. No referral tracking.",
      strategy: "Google Business optimization, systematic review collection, patient referral program, and targeted health awareness content.",
      outcome: "Projected 50% increase in new patient inquiries and improved local search ranking.",
      color: "#EC4899"
    },
    {
      icon: Wrench,
      type: "Service Business",
      title: "Home Services Scaling – Pune",
      problem: "Unable to scale beyond personal capacity. Inconsistent lead flow and pricing challenges.",
      diagnosis: "No standardized service packages. Missing follow-up systems. Weak brand differentiation.",
      strategy: "Create tiered service packages, implement CRM for lead tracking, build review generation system, and develop brand positioning.",
      outcome: "Expected 60% improvement in lead conversion and foundation for team expansion.",
      color: "#0062D6"
    }
  ];

  return (
    <section id="case-studies" ref={ref} className="section bg-alt" data-testid="case-studies-section">
      <div className="container">
        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mb-16">
          <span className="section-label">Strategic Analyses</span>
          <h2 className="section-title">Case Studies & Business Analyses</h2>
          <p className="section-description mx-auto">See how we diagnose problems and design growth systems for different industries.</p>
        </motion.div>

        <motion.div variants={staggerContainer} initial="hidden" animate={isInView ? "visible" : "hidden"} className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {caseStudies.map((study, i) => (
            <motion.div 
              key={i} 
              variants={fadeInUp} 
              className="case-study-card bg-white rounded-2xl border border-[#E0EFFF] overflow-hidden hover:shadow-xl transition-shadow"
              whileHover={{ y: -5 }}
              data-testid={`case-study-card-${i}`}
            >
              {/* Header */}
              <div className="p-6 border-b border-[#E0EFFF]">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${study.color}15` }}>
                    <study.icon size={24} style={{ color: study.color }} />
                  </div>
                  <div>
                    <span className="text-xs font-medium uppercase tracking-wider" style={{ color: study.color }}>{study.type}</span>
                    <h3 className="font-heading font-bold text-lg text-[#0A1628]">{study.title}</h3>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle size={16} className="text-red-500" />
                    <span className="font-semibold text-sm text-red-600">Problem Identified</span>
                  </div>
                  <p className="text-sm text-[#004BA8]">{study.problem}</p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Search size={16} className="text-amber-500" />
                    <span className="font-semibold text-sm text-amber-600">Diagnosis</span>
                  </div>
                  <p className="text-sm text-[#004BA8]">{study.diagnosis}</p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb size={16} className="text-blue-500" />
                    <span className="font-semibold text-sm text-blue-600">Strategy Proposed</span>
                  </div>
                  <p className="text-sm text-[#004BA8]">{study.strategy}</p>
                </div>

                <div className="pt-4 border-t border-[#E0EFFF]">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp size={16} className="text-green-500" />
                    <span className="font-semibold text-sm text-green-600">Expected Outcome</span>
                  </div>
                  <p className="text-sm font-medium text-green-700">{study.outcome}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mt-12">
          <p className="text-[#004BA8] mb-4">Want a similar analysis for your business?</p>
          <motion.a href="#diagnostic" className="btn-primary" whileHover={{ scale: 1.03 }}>
            Get Your Free Diagnostic<ArrowRight size={18} />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

// Business Insights Section (NEW)
const InsightsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const insights = [
    {
      title: "Why Most Gyms Struggle to Cross 300 Members",
      description: "The membership plateau is rarely about marketing. It's about retention systems, referral mechanics, and the invisible churn that drains growth before it happens.",
      category: "Fitness Industry",
      readTime: "5 min read"
    },
    {
      title: "Hidden Revenue Leaks in Small Service Businesses",
      description: "Most service businesses lose 20-30% of potential revenue to poor follow-up systems, inconsistent pricing, and missed upsell opportunities.",
      category: "Service Business",
      readTime: "4 min read"
    },
    {
      title: "Why Clinics Lose Patients to Larger Chains",
      description: "It's not about facilities or pricing. It's about perceived expertise, trust signals, and the patient experience gap that drives decisions.",
      category: "Healthcare",
      readTime: "6 min read"
    },
    {
      title: "The Growth Systems Most Local Businesses Are Missing",
      description: "Three interconnected systems—acquisition, conversion, retention—determine whether a business scales or stagnates. Most focus on just one.",
      category: "Business Strategy",
      readTime: "7 min read"
    }
  ];

  return (
    <section id="insights" ref={ref} className="section bg-light" data-testid="insights-section">
      <div className="container">
        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mb-16">
          <span className="section-label">Knowledge</span>
          <h2 className="section-title">Business Insights & Market Research</h2>
          <p className="section-description mx-auto">Strategic thinking and analysis to help you understand what drives business growth.</p>
        </motion.div>

        <motion.div variants={staggerContainer} initial="hidden" animate={isInView ? "visible" : "hidden"} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {insights.map((insight, i) => (
            <motion.div 
              key={i} 
              variants={fadeInUp} 
              className="group p-6 bg-white rounded-xl border border-[#E0EFFF] hover:border-[#00D4FF]/30 hover:shadow-lg transition-all cursor-pointer"
              whileHover={{ y: -3 }}
              data-testid={`insight-card-${i}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <BookOpen size={20} className="text-[#00D4FF]" />
                <span className="text-xs font-medium uppercase tracking-wider text-[#0062D6]">{insight.category}</span>
                <span className="text-xs text-[#004BA8]/60 ml-auto">{insight.readTime}</span>
              </div>
              <h3 className="font-heading font-bold text-lg text-[#0A1628] mb-3 group-hover:text-[#0062D6] transition-colors">{insight.title}</h3>
              <p className="text-sm text-[#004BA8] leading-relaxed mb-4">{insight.description}</p>
              <div className="flex items-center gap-2 text-[#0062D6] text-sm font-medium group-hover:gap-3 transition-all">
                <span>Read More</span>
                <ArrowRight size={16} />
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Founder/Strategist Section (NEW)
const FounderSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="section bg-dark text-white" data-testid="founder-section">
      <div className="container">
        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mb-12">
          <span className="text-[#00D4FF] text-sm font-bold tracking-wider uppercase mb-4 block">Leadership</span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold">Meet the Strategist Behind NITIA</h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
          {/* Photo */}
          <motion.div variants={slideFromLeft} initial="hidden" animate={isInView ? "visible" : "hidden"} className="lg:col-span-2">
            <div className="relative max-w-sm mx-auto">
              <div className="aspect-[4/5] rounded-2xl bg-gradient-to-br from-[#003573] to-[#0A1628] p-1">
                <div className="w-full h-full rounded-xl bg-gradient-to-br from-[#001F45] to-[#003573] flex items-center justify-center overflow-hidden">
                  <div className="text-center p-8">
                    <div className="w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-[#00D4FF] to-[#0062D6] flex items-center justify-center mb-6">
                      <User size={64} className="text-white" />
                    </div>
                    <p className="text-white/60 text-sm">Strategic Growth Consultant</p>
                  </div>
                </div>
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 border-2 border-[#00D4FF]/20 rounded-xl" />
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-[#00D4FF]/10 rounded-lg" />
            </div>
          </motion.div>

          {/* Content */}
          <motion.div variants={slideFromRight} initial="hidden" animate={isInView ? "visible" : "hidden"} className="lg:col-span-3 space-y-6">
            <div>
              <h3 className="font-heading text-2xl font-bold mb-2">Strategic Philosophy</h3>
              <p className="text-white/70 leading-relaxed">
                Every business has hidden growth potential. The challenge isn't finding more tactics or tools—it's understanding the specific constraints holding your business back. At NITIA, we believe in diagnosis before prescription.
              </p>
            </div>

            <div>
              <h3 className="font-heading text-2xl font-bold mb-2">Our Approach</h3>
              <p className="text-white/70 leading-relaxed">
                We don't offer generic solutions. Instead, we analyze your business through three lenses: how you acquire customers, how you convert them, and how efficiently you deliver value. This systematic approach reveals the exact bottlenecks limiting your growth.
              </p>
            </div>

            <div>
              <h3 className="font-heading text-2xl font-bold mb-2">The Mission</h3>
              <p className="text-white/70 leading-relaxed">
                Our mission is to help service businesses and SMEs unlock their growth potential through strategic systems—not guesswork. We combine deep business analysis with practical implementation to create measurable, sustainable growth.
              </p>
            </div>

            <div className="pt-6 flex flex-wrap gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#00D4FF]/20 flex items-center justify-center">
                  <Target size={20} className="text-[#00D4FF]" />
                </div>
                <div>
                  <p className="text-white font-semibold">Strategic Focus</p>
                  <p className="text-white/50 text-sm">Systems over tactics</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#A855F7]/20 flex items-center justify-center">
                  <LineChart size={20} className="text-[#A855F7]" />
                </div>
                <div>
                  <p className="text-white font-semibold">Data-Driven</p>
                  <p className="text-white/50 text-sm">Analysis before action</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Trust Signals Strip (NEW)
const TrustSignals = () => {
  const stats = [
    { number: "50+", label: "Businesses Analyzed" },
    { number: "10+", label: "Industries Studied" },
    { number: "15+", label: "Strategic Frameworks" }
  ];

  return (
    <div className="bg-gradient-to-r from-[#0A1628] via-[#003573] to-[#0A1628] py-8" data-testid="trust-signals">
      <div className="container">
        <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
          {stats.map((stat, i) => (
            <div key={i} className="text-center">
              <p className="font-heading text-3xl md:text-4xl font-bold text-[#00D4FF]">{stat.number}</p>
              <p className="text-white/60 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Business Growth Diagnostic Form (NEW - Replaces Growth Audit)
// FAQ Section - Common Questions
const FAQSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What is a Growth Audit?",
      answer: "A Growth Audit is a comprehensive analysis of your business's current growth systems, customer acquisition processes, and revenue operations. We identify gaps, bottlenecks, and opportunities that are limiting your growth potential. The audit provides actionable insights and a clear roadmap for building scalable systems."
    },
    {
      question: "How long does the consulting process take?",
      answer: "The timeline depends on the scope and complexity of your business needs. A typical engagement begins with a 2-week deep dive, followed by a 4-6 week strategy and system design phase. Implementation support can extend from 3-6 months depending on the scale of changes required. We focus on building sustainable systems, not quick fixes."
    },
    {
      question: "What results can I expect?",
      answer: "Our clients typically see improvements in customer acquisition efficiency, revenue predictability, and operational clarity. Specific outcomes vary by business, but common results include: clearer growth direction, more structured sales processes, better lead conversion rates, and scalable systems that reduce dependency on individual efforts. We focus on measurable, sustainable growth rather than short-term spikes."
    },
    {
      question: "Do you only provide strategy, or do you also help with execution?",
      answer: "We don't stop at strategy. We help design and guide execution to ensure systems actually work in practice. Our approach combines strategic planning with hands-on support during implementation. We believe strategy without execution is just theory — and execution without strategy is just activity. Both need to work together."
    },
    {
      question: "What types of businesses do you work with?",
      answer: "We work primarily with growth-stage businesses generating ₹5 Cr – ₹100 Cr in annual revenue. Our clients are typically founders and CEOs who have achieved initial success but recognize that their current systems won't support the next level of growth. We work across industries including professional services, healthcare, manufacturing, retail, and technology."
    },
    {
      question: "How is NITIA different from a marketing agency?",
      answer: "Marketing agencies focus on campaigns and lead generation. We focus on building the complete system that turns leads into customers and customers into revenue. We're not here to run your ads or manage your social media. We're here to design the strategic infrastructure that makes all your growth efforts more effective and sustainable."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" ref={ref} className="section bg-alt" data-testid="faq-section">
      <div className="container">
        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mb-12">
          <span className="section-label">Common Questions</span>
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="section-description mx-auto">Everything you need to know about working with us.</p>
        </motion.div>

        <motion.div variants={staggerContainer} initial="hidden" animate={isInView ? "visible" : "hidden"} className="max-w-3xl mx-auto">
          {faqs.map((faq, index) => (
            <motion.div 
              key={index} 
              variants={fadeInUp}
              className="mb-4"
              data-testid={`faq-item-${index}`}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className={`w-full text-left p-5 rounded-xl border transition-all ${
                  openIndex === index 
                    ? 'bg-white border-[#00D4FF] shadow-lg' 
                    : 'bg-white border-[#E0EFFF] hover:border-[#00D4FF]/50'
                }`}
              >
                <div className="flex items-center justify-between gap-4">
                  <h3 className="font-heading font-semibold text-[#0A1628] text-lg">{faq.question}</h3>
                  <motion.div
                    animate={{ rotate: openIndex === index ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex-shrink-0"
                  >
                    <ChevronDown size={20} className={openIndex === index ? 'text-[#00D4FF]' : 'text-[#004BA8]'} />
                  </motion.div>
                </div>
                
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <p className="mt-4 text-[#004BA8] leading-relaxed pt-4 border-t border-[#E0EFFF]">
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA after FAQ */}
        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mt-12">
          <p className="text-[#004BA8] mb-6">Still have questions? Let's talk.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <motion.a 
              href={`mailto:${CONTACT_EMAIL}`}
              className="btn-secondary"
              whileHover={{ scale: 1.05 }}
            >
              <Mail size={18} />
              Email Us
            </motion.a>
            <motion.a 
              href="#"
              onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="floating-audit-trigger"]')?.click(); }}
              className="btn-primary"
              whileHover={{ scale: 1.05 }}
            >
              <Zap size={18} />
              Get Free Growth Audit
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Strategic Questions Section (NEW)
const StrategicQuestionsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const questions = [
    {
      question: "Why do some companies stop scaling after a certain stage of growth?",
      insight: "Often, the systems that helped a company reach one level become the bottlenecks preventing the next."
    },
    {
      question: "Why do businesses generate leads but struggle to convert them into customers?",
      insight: "The gap between acquisition and conversion usually reveals structural weaknesses in sales processes."
    },
    {
      question: "Why do operations become chaotic as companies grow?",
      insight: "Scaling without systems creates complexity. Complexity without structure creates chaos."
    },
    {
      question: "Why do some organizations grow quickly while others stagnate despite strong demand?",
      insight: "Growth velocity is rarely about effort—it's about system design and constraint identification."
    }
  ];

  return (
    <section id="strategic-questions" ref={ref} className="section bg-dark text-white" data-testid="strategic-questions-section">
      <div className="container">
        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mb-16">
          <span className="text-[#00D4FF] text-sm font-bold tracking-wider uppercase mb-4 block">Deep Questions</span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">Strategic Questions We Explore</h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">These are the questions we investigate when analyzing companies. They often reveal the hidden constraints limiting growth.</p>
        </motion.div>

        <motion.div variants={staggerContainer} initial="hidden" animate={isInView ? "visible" : "hidden"} className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {questions.map((item, i) => (
            <motion.div 
              key={i} 
              variants={fadeInUp} 
              className="p-6 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm hover:border-[#00D4FF]/30 transition-colors"
              whileHover={{ y: -3 }}
              data-testid={`strategic-question-${i}`}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#00D4FF]/20 flex items-center justify-center flex-shrink-0">
                  <HelpCircle size={20} className="text-[#00D4FF]" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-white leading-tight">{item.question}</h3>
              </div>
              <p className="text-white/60 text-sm leading-relaxed pl-14">{item.insight}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mt-12">
          <p className="text-white/60 mb-6">If these questions resonate with your company's challenges...</p>
          <motion.a href="#diagnostic" className="btn-primary" whileHover={{ scale: 1.03 }}>
            Request a Growth Diagnostic<ArrowRight size={18} />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

// Opportunities Section (NEW) - Freelance & Strategy Internship
const OpportunitiesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    linkedin: '',
    areaOfInterest: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submit-opportunities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Submission failed');
      }
      
      const result = await response.json();
      console.log('Form submitted successfully:', result);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitError('Something went wrong. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const internResponsibilities = [
    "Industry research and market analysis",
    "Studying business models of companies",
    "Preparing consulting case breakdowns",
    "Assisting with strategic frameworks"
  ];

  const freelanceAreas = [
    { icon: FileSearch, text: "Market research" },
    { icon: BarChart3, text: "Competitive analysis" },
    { icon: Database, text: "Business data analysis" },
    { icon: PenTool, text: "Content writing for business insights" },
    { icon: Globe, text: "Website and design support" }
  ];

  const whoShouldApply = [
    "Students interested in consulting or entrepreneurship",
    "Freelancers interested in business strategy projects",
    "Researchers who enjoy analyzing industries and markets",
    "Individuals curious about how companies grow and scale"
  ];

  return (
    <section id="opportunities" ref={ref} className="section bg-alt" data-testid="opportunities-section">
      <div className="container">
        <motion.div variants={fadeInUp} initial="hidden" animate={isInView ? "visible" : "hidden"} className="text-center mb-16">
          <span className="section-label">Join Us</span>
          <h2 className="section-title">Freelance & Strategy Internship Opportunities</h2>
          <p className="section-description mx-auto">At NITIA, we constantly analyze businesses, industries, and growth systems. We occasionally collaborate with researchers, freelancers, and students who want to learn how real business strategy works.</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Opportunity Types */}
          <motion.div variants={slideFromLeft} initial="hidden" animate={isInView ? "visible" : "hidden"} className="space-y-8">
            {/* Strategy Research Intern */}
            <div className="bg-white rounded-2xl border border-[#E0EFFF] p-6 shadow-sm">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#A855F7]/10 flex items-center justify-center">
                  <GraduationCap size={24} className="text-[#A855F7]" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-[#0A1628]">Strategy Research Intern</h3>
                  <span className="text-xs font-medium text-[#A855F7] uppercase tracking-wider">Unpaid Learning Internship</span>
                </div>
              </div>
              <p className="text-[#004BA8] text-sm mb-4">This internship is designed for people who want to learn how consulting firms analyze businesses.</p>
              <p className="text-sm font-medium text-[#0A1628] mb-3">Responsibilities may include:</p>
              <ul className="space-y-2">
                {internResponsibilities.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#004BA8]">
                    <CheckCircle2 size={16} className="text-[#A855F7] mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Freelance Contributors */}
            <div className="bg-white rounded-2xl border border-[#E0EFFF] p-6 shadow-sm">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#00D4FF]/10 flex items-center justify-center">
                  <Handshake size={24} className="text-[#00D4FF]" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-[#0A1628]">Freelance Contributors</h3>
                  <span className="text-xs font-medium text-[#00D4FF] uppercase tracking-wider">Project-Based Collaboration</span>
                </div>
              </div>
              <p className="text-[#004BA8] text-sm mb-4">We collaborate with freelancers on specific projects depending on availability and expertise.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {freelanceAreas.map((area, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-[#004BA8]">
                    <area.icon size={16} className="text-[#00D4FF]" />
                    {area.text}
                  </div>
                ))}
              </div>
            </div>

            {/* Who Should Apply */}
            <div className="bg-gradient-to-br from-[#0A1628] to-[#003573] rounded-2xl p-6 text-white">
              <h3 className="font-heading font-bold text-lg mb-4">Who Should Apply?</h3>
              <ul className="space-y-3">
                {whoShouldApply.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                    <ArrowRight size={16} className="text-[#00D4FF] mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Application Form */}
          <motion.div variants={slideFromRight} initial="hidden" animate={isInView ? "visible" : "hidden"}>
            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-[#E0EFFF] p-8 shadow-lg" data-testid="opportunities-form">
                <h3 className="font-heading font-bold text-xl text-[#0A1628] mb-6">Apply to Collaborate with NITIA</h3>
                
                <div className="space-y-5">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-[#0A1628] mb-2">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-[#E0EFFF] focus:border-[#00D4FF] focus:ring-2 focus:ring-[#00D4FF]/20 outline-none transition-all"
                      placeholder="Your full name"
                      data-testid="opp-input-name"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-[#0A1628] mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-[#E0EFFF] focus:border-[#00D4FF] focus:ring-2 focus:ring-[#00D4FF]/20 outline-none transition-all"
                      placeholder="your@email.com"
                      data-testid="opp-input-email"
                    />
                  </div>

                  {/* LinkedIn */}
                  <div>
                    <label className="block text-sm font-medium text-[#0A1628] mb-2">LinkedIn Profile</label>
                    <input
                      type="url"
                      name="linkedin"
                      value={formData.linkedin}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-[#E0EFFF] focus:border-[#00D4FF] focus:ring-2 focus:ring-[#00D4FF]/20 outline-none transition-all"
                      placeholder="https://linkedin.com/in/yourprofile"
                      data-testid="opp-input-linkedin"
                    />
                  </div>

                  {/* Area of Interest */}
                  <div>
                    <label className="block text-sm font-medium text-[#0A1628] mb-2">Area of Interest *</label>
                    <select
                      name="areaOfInterest"
                      value={formData.areaOfInterest}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-[#E0EFFF] focus:border-[#00D4FF] focus:ring-2 focus:ring-[#00D4FF]/20 outline-none transition-all bg-white"
                      data-testid="opp-input-interest"
                    >
                      <option value="">Select your interest</option>
                      <option value="Strategy Research Internship">Strategy Research Internship</option>
                      <option value="Freelance - Market Research">Freelance - Market Research</option>
                      <option value="Freelance - Content Writing">Freelance - Content Writing</option>
                      <option value="Freelance - Design Support">Freelance - Design Support</option>
                      <option value="Freelance - Data Analysis">Freelance - Data Analysis</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-[#0A1628] mb-2">Why are you interested in working with NITIA? *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full px-4 py-3 rounded-lg border border-[#E0EFFF] focus:border-[#00D4FF] focus:ring-2 focus:ring-[#00D4FF]/20 outline-none transition-all resize-none"
                      placeholder="Tell us about your background and why you're interested in business strategy..."
                      data-testid="opp-input-message"
                    />
                  </div>
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full mt-6 btn-primary justify-center"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="submit-opportunities"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Submit Application
                    </>
                  )}
                </motion.button>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-2xl border border-[#E0EFFF] p-12 shadow-lg text-center"
                data-testid="opportunities-success"
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 size={40} className="text-green-600" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-[#0A1628] mb-4">Application Received!</h3>
                <p className="text-[#004BA8] text-lg mb-6">
                  Thank you for your interest in collaborating with NITIA.
                </p>
                <p className="text-sm text-[#004BA8]/70">
                  We review applications periodically and will reach out if there is a suitable opportunity.
                </p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Contact Section (Final CTA)
const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="contact" ref={ref} className="section bg-dark text-white" data-testid="contact-section">
      <div className="container">
        <motion.div variants={staggerContainer} initial="hidden" animate={isInView ? "visible" : "hidden"} className="max-w-2xl mx-auto text-center">
          <motion.a 
            href="#"
            onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="floating-audit-trigger"]')?.click(); }}
            variants={fadeInUp} 
            className="btn-primary text-lg px-10 py-5" 
            whileHover={{ scale: 1.05, y: -3 }} 
            data-testid="contact-cta"
          >
            Get Your Growth Diagnosis<ArrowRight size={22} />
          </motion.a>
          <motion.p variants={fadeInUp} className="mt-6 text-white/60 text-lg">
            Let's understand your business and identify where growth is breaking.
          </motion.p>
          <motion.p variants={fadeInUp} className="mt-8 text-white/40 text-sm">
            Or email us directly at <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#00D4FF] hover:underline">{CONTACT_EMAIL}</a>
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="footer" data-testid="footer">
    <div className="container">
      <div className="footer-inner">
        <div className="footer-brand">
          <img src={LOGO_URL} alt="NITIA Logo" />
          <div>
            <span className="footer-brand-text">NITIA Growth Systems</span>
            <p className="text-xs text-white/40 mt-1">Strategic Business Consulting</p>
          </div>
        </div>
        <p className="footer-copyright">© {new Date().getFullYear()} NITIA Growth Systems. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

// Floating Growth Audit Widget
const FloatingGrowthAudit = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    businessName: '',
    industry: '',
    city: '',
    challenge: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsVisible(window.scrollY > window.innerHeight * 0.3);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);

  try {
    const response = await fetch("/api/audit", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        ...formData
      })
    });

    const result = await response.json();

    if (response.ok) {
      setIsSubmitted(true);
      setFormData({
        name: "",
        businessName: "",
        industry: "",
        city: "",
        challenge: ""
      });
    } else {
      alert(result.error || "Something went wrong.");
    }
  } catch (error) {
    alert("Failed to send audit request.");
  } finally {
    setIsSubmitting(false);
  }
};

  const industryOptions = [
    "Healthcare/Clinic", "Fitness/Gym", "Restaurant/F&B", "Professional Services",
    "Retail/E-commerce", "Education/Coaching", "Real Estate", "Manufacturing", "Other"
  ];

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-6 right-6 z-50"
          data-testid="floating-audit-widget"
        >
          {/* Collapsed Button */}
          {!isOpen && (
            <motion.button
              onClick={() => setIsOpen(true)}
              className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-[#0A1628] to-[#003573] text-white rounded-full shadow-xl hover:shadow-2xl transition-shadow"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              data-testid="floating-audit-trigger"
            >
              <div className="w-10 h-10 rounded-full bg-[#00D4FF]/20 flex items-center justify-center">
                <Zap size={20} className="text-[#00D4FF]" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold">Free Growth Audit</p>
                <p className="text-xs text-white/60">Get insights in 48 hours</p>
              </div>
            </motion.button>
          )}

          {/* Expanded Widget */}
          {isOpen && (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-[340px] bg-white rounded-2xl shadow-2xl border border-[#E0EFFF] overflow-hidden"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#0A1628] to-[#003573] p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#00D4FF]/20 flex items-center justify-center">
                    <Zap size={20} className="text-[#00D4FF]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">Free Growth Audit</p>
                    <p className="text-white/60 text-xs">Quick 2-minute form</p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Form Content */}
              <div className="p-4">
                {!isSubmitted ? (
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your Name *"
                        className="w-full px-3 py-2.5 rounded-lg border border-[#E0EFFF] focus:border-[#00D4FF] focus:ring-1 focus:ring-[#00D4FF]/20 outline-none text-sm"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        name="businessName"
                        value={formData.businessName}
                        onChange={handleChange}
                        required
                        placeholder="Business Name *"
                        className="w-full px-3 py-2.5 rounded-lg border border-[#E0EFFF] focus:border-[#00D4FF] focus:ring-1 focus:ring-[#00D4FF]/20 outline-none text-sm"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        name="industry"
                        value={formData.industry}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2.5 rounded-lg border border-[#E0EFFF] focus:border-[#00D4FF] outline-none text-sm bg-white"
                      >
                        <option value="">Industry *</option>
                        {industryOptions.map((opt, i) => (
                          <option key={i} value={opt}>{opt}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        placeholder="City *"
                        className="w-full px-3 py-2.5 rounded-lg border border-[#E0EFFF] focus:border-[#00D4FF] outline-none text-sm"
                      />
                    </div>
                    <div>
                      <textarea
                        name="challenge"
                        value={formData.challenge}
                        onChange={handleChange}
                        required
                        rows={2}
                        placeholder="Biggest growth challenge *"
                        className="w-full px-3 py-2.5 rounded-lg border border-[#E0EFFF] focus:border-[#00D4FF] outline-none text-sm resize-none"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-3 bg-gradient-to-r from-[#00D4FF] to-[#0062D6] text-[#0A1628] font-semibold rounded-lg hover:shadow-lg transition-shadow flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <div className="w-5 h-5 border-2 border-[#0A1628]/30 border-t-[#0A1628] rounded-full animate-spin" />
                      ) : (
                        <>
                          <Send size={16} />
                          Get Free Audit
                        </>
                      )}
                    </button>
                    <p className="text-xs text-center text-[#666666]">
                      We'll analyze your business and respond within 48 hours.
                    </p>
                    <div className="pt-3 mt-3 border-t border-[#E0EFFF] text-center">
                      <p className="text-xs text-[#666666] mb-1">Or email us directly at</p>
                      <a 
                        href={`mailto:${CONTACT_EMAIL}`}
                        className="text-sm font-medium text-[#0062D6] hover:underline"
                      >
                        {CONTACT_EMAIL}
                      </a>
                    </div>
                  </form>
                ) : (
                  <div className="text-center py-6">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle2 size={28} className="text-green-600" />
                    </div>
                    <h4 className="font-heading font-bold text-[#0A1628] mb-2">Thank You!</h4>
                    <p className="text-sm text-[#004BA8]">We'll review your business and share insights within 48 hours.</p>
                    <button
                      onClick={() => { setIsSubmitted(false); setIsOpen(false); setFormData({ name: '', businessName: '', industry: '', city: '', challenge: '' }); }}
                      className="mt-4 text-sm text-[#0062D6] hover:underline"
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Main App - Section Order: Hero > How We Think > Work/Clients > Insights > Who We Work With > How We Work > About > Feedback > FAQ
function App() {
  return (
    <div className="App">
      <Navbar />
      <main>
        <HeroSection />
        <TrustSignals />
        <HowWeThinkSection />
        <SelectedWorkSection />
        <StrategicCaseBreakdowns />
        <WhoWeHelpSection />
        <HowWeWorkSection />
        <AboutSection />
        <ClientFeedbackSection />
        <FAQSection />
        <ContactSection />
      </main>
      <Footer />
      <FloatingGrowthAudit />
    </div>
  );
}

export default App;
